/**
 * BusinessDocumentsTab.jsx
 *
 * - Upload business-level documents
 * - Supports expiration tracking
 * - Metadata saved via GraphQL
 * - Notification-ready
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { uploadToCloudinary } from "../../../util/upload/uploadToCloudinary";
import DocumentsTab from "../documents/DocumentsTab";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { UserContext } from "../../auth/auth-context";
import { useSelectedBusiness } from "../../../redux/store";
import { FeatureAccess, getFeatureAccess } from "../../../util/featureAccess";

const BUSINESS_DOCUMENT_TYPES = [
    { value: "LICENSE", label: "License" },
    { value: "INSURANCE", label: "Insurance" },
    { value: "POLICY", label: "Policy" },
    { value: "COMPLIANCE", label: "Compliance" },
    { value: "CONTRACT", label: "Contract" },
    { value: "OTHER", label: "Other" },
];

const BUSINESS_DOCUMENT_TYPE_LABELS = BUSINESS_DOCUMENT_TYPES.reduce((acc, type) => {
    acc[type.value] = type.label;
    return acc;
}, {});

const GET_BUSINESS_DOCUMENTS = gql`
  query GetBusinessDocuments($businessId: ID!) {
    getBusinessDocuments(businessId: $businessId) {
      id
      fileName
      fileUrl
      type
      expiresAt
      createdAt
    }
  }
`;

const GENERATE_UPLOAD_URL = gql`
  mutation GenerateUploadUrl($filename: String!, $contentType: String!) {
    generateUploadUrl(filename: $filename, contentType: $contentType)
  }
`;

const ADD_BUSINESS_DOCUMENT = gql`
  mutation AddBusinessDocument(
    $businessId: ID!
    $type: String!
    $fileUrl: String!
    $fileName: String
    $size: Int
    $staffId: ID
    $expiresAt: DateTime
  ) {
    addBusinessDocument(
      businessId: $businessId
      type: $type
      fileUrl: $fileUrl
      fileName: $fileName
      size: $size
      staffId: $staffId
      expiresAt: $expiresAt
    ) {
      id
    }
  }
`;

const DELETE_BUSINESS_DOCUMENT = gql`
  mutation DeleteBusinessDocument($id: ID!) {
    deleteBusinessDocument(id: $id)
  }
`;

export default function BusinessDocumentsTab({ businessId, staffId, isDisabled = false }) {
    const { providerUser } = useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const fileInputRef = useRef(null);
    const normalizedBusinessId = useMemo(() => {
        if (businessId == null || businessId === "") return null;
        const asNumber = Number(businessId);
        return Number.isFinite(asNumber) && asNumber > 0
            ? asNumber
            : String(businessId);
    }, [businessId]);

    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState("OTHER");
    const [customTypeName, setCustomTypeName] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const documentAccess = getFeatureAccess(providerUser, FeatureAccess.DOCUMENT_MANAGEMENT);
    const standardPlanRequired = documentAccess.requiredPlanId;
    const hasStandardDocAccess = documentAccess.hasAccess;
    const currentLoginStaff = selectedBusiness?.businessStaffs?.find(
        (s) => Number(s?.id) === Number(providerUser?.staffId)
    );
    const isAdmin = String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
    const docsReadOnly = Boolean(isDisabled || !hasStandardDocAccess || !isAdmin);

    const { data, refetch } = useQuery(GET_BUSINESS_DOCUMENTS, {
        variables: { businessId: normalizedBusinessId },
        skip: !normalizedBusinessId || !isAdmin,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
    });

    useEffect(() => {
        if (!normalizedBusinessId || !isAdmin || typeof refetch !== "function") return;
        refetch({ businessId: normalizedBusinessId });
    }, [normalizedBusinessId, isAdmin, refetch]);

    const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
    const [addBusinessDocument] = useMutation(ADD_BUSINESS_DOCUMENT);
    const [deleteBusinessDocument] = useMutation(DELETE_BUSINESS_DOCUMENT);

    const documents = data?.getBusinessDocuments || [];

    const handleSelectFile = () => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);

        try {
            let fileUrl;

            if (process.env.REACT_APP_ENV === "development") {
                fileUrl = await uploadToCloudinary(file);
            } else {
                const filename = `business-documents/${normalizedBusinessId}/${Date.now()}-${file.name}`;

                const { data: uploadData } = await generateUploadUrl({
                    variables: {
                        filename,
                        contentType: file.type,
                    },
                });

                const signedUrl = uploadData.generateUploadUrl;

                await fetch(signedUrl, {
                    method: "PUT",
                    headers: { "Content-Type": file.type },
                    body: file,
                });

                fileUrl = signedUrl.split("?")[0];
            }

            await addBusinessDocument({
                variables: {
                    businessId: normalizedBusinessId,
                    type:
                        docType === "OTHER" && customTypeName
                            ? customTypeName
                            : docType,
                    fileUrl,
                    fileName: file.name,
                    size: file.size,
                    staffId: staffId || null,
                    expiresAt: expiresAt || null,
                },
            });

            await refetch();
        } catch (err) {
            console.error("Business document upload failed:", err);
            alert("Failed to upload document");
        } finally {
            setUploading(false);
            setExpiresAt("");
            setCustomTypeName("");
            e.target.value = "";
        }
    };

    const handleDelete = async (docId) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        if (!window.confirm("Delete this document?")) return;
        try {
            await deleteBusinessDocument({ variables: { id: docId } });
            await refetch();
        } catch (err) {
            console.error("Business document delete failed:", err);
            alert("Failed to delete document");
        }
    };

    return (
        <>
            {!isAdmin && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Admin role required to view business documents.
                </Typography>
            )}
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName="Document upload and updates"
                    requiredPlanId={standardPlanRequired}
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}
            {isAdmin && !hasStandardDocAccess && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Standard plan required to upload or delete documents.
                </Typography>
            )}
            {isAdmin && (
                <DocumentsTab
                title="Business Documents"
                documentTypes={BUSINESS_DOCUMENT_TYPES}
                documents={documents}
                docType={docType}
                onChangeDocType={setDocType}
                customTypeName={customTypeName}
                onChangeCustomTypeName={setCustomTypeName}
                expiresAt={expiresAt}
                onChangeExpiresAt={setExpiresAt}
                uploading={uploading}
                onSelectFile={handleSelectFile}
                onFileSelected={handleFileSelected}
                onDelete={handleDelete}
                fileInputRef={fileInputRef}
                groupByType
                typeLabelMap={BUSINESS_DOCUMENT_TYPE_LABELS}
                isDisabled={docsReadOnly}
            />
            )}
        </>
    );
}
